/**
 * Static lookup for lucide-react icons referenced dynamically by name.
 *
 * The 4 components that previously did `import * as Icons from 'lucide-react'`
 * (menu-renderer, social-menu-renderer, ai-logic/ToolCard, ai-logic/ToolDetailPanel)
 * defeated tree-shaking and dragged the entire ~1500-icon library into the
 * public bundle (~530 KB raw). This map lists only the icons actually stored
 * in CMS data (menu_items.icon, ai_tools.icon) plus a few likely additions,
 * so the bundle ships ~30 icons instead of the whole library.
 *
 * To add a new icon usable from CMS data: add a static import and a kebab-
 * cased entry to `iconMap` below.
 */

import {
    type LucideIcon,
    // Social platforms (menu_items.icon)
    Facebook,
    Github,
    Instagram,
    Linkedin,
    Twitter,
    Twitch,
    Youtube,
    // AI tools (ai_tools.icon)
    CalendarRange,
    CopyCheck,
    FilePen,
    GitBranch,
    GraduationCap,
    Palette,
    ScanSearch,
    Search,
    SpellCheck,
    UserPen,
    WandSparkles,
} from 'lucide-react';

const iconMap: Record<string, LucideIcon> = {
    // Social platforms
    facebook: Facebook,
    github: Github,
    instagram: Instagram,
    linkedin: Linkedin,
    twitter: Twitter,
    twitch: Twitch,
    x: Twitter,
    youtube: Youtube,

    // AI tools
    'calendar-range': CalendarRange,
    'copy-check': CopyCheck,
    'file-pen': FilePen,
    'git-branch': GitBranch,
    'graduation-cap': GraduationCap,
    palette: Palette,
    'scan-search': ScanSearch,
    search: Search,
    'spell-check': SpellCheck,
    'user-pen': UserPen,
    'wand-sparkles': WandSparkles,
};

/**
 * Look up a lucide icon by name. Accepts kebab-case ('calendar-range'),
 * PascalCase ('CalendarRange'), or normalized lowercase ('calendarrange').
 * Returns null when the name isn't in the allowlist — callers decide the
 * fallback (e.g. <Bot /> on AI tools, no icon on menus).
 */
export function getIconByName(name: string | null | undefined): LucideIcon | null {
    if (!name) return null;
    // Normalize: PascalCase → kebab, lower-case, collapse whitespace/underscores
    const kebab = name
        .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
        .toLowerCase()
        .replace(/[\s_]+/g, '-');
    return iconMap[kebab] ?? null;
}
